import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormInstance } from 'antd/lib';
import { useForm } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';

import MasterService from '../../../../../../../shared/services/master';
import ProducersService from '../../../../../../../shared/services/producers';
import QuotationService from '../../../../../../../shared/services/quotation';

import { GenericDataFormContext } from './GenericDataFormContext';
import { QuotationInfoContext } from '../QuotationInfo';

import {
  AdditionalDataClient,
  BankAccount,
  CoveragePerPlan,
  CreditCardsInsured,
  CurrencyCode,
  GetLocalitiesListPerProvinceResponse,
  Locality,
  PaymentMethodForms,
  QuotationClient,
  RequestCoveragesByPlanActivities,
  SaveQuotationServiceRequest,
  SelectOption,
} from '../../../../../../../shared/interfaces';

import {
  checkValue,
  checkValueAndReturnDefault,
  displayErrorMessage,
  displaySuccessMessage,
  reIndexComponents,
} from '../../../../../../../shared/utils';
import { TextSubcategory } from '../../../../../../../shared/interfaces/services/get-list-text-subcategory';
import { RequestListTextSubcategory } from '../../../../../../../shared/interfaces/services/request-list-text-subcategory';
import { QuotationArticles } from '../../../utils/quotation-articles.enum';
import { ClientParam } from '../../../../../../../shared/interfaces/services/save-quotation-service';
import { StepOneFormContext } from '../StepOneForm';
import dayjs from 'dayjs';

export interface ClientDataSectionForm {
  address:            string;
  contractDate:       any;
  cuit:               number;
  documentNumber:     number;
  documentType:       number;
  fullName:           string;
  genderType:         number;
  inscriptionDate:    any;
  inscriptionNumber:  string;
  ivaCondition:       number;
  location:           string;
  nationality:        number;
  perceptionAgent:    string;
  personType:         string;
  province:           string;
  societyType:        number;
}

export interface AdditionalDataSectionForm {
  addressEmail:       string;
  birthDate:          any;
  cellphoneNumber:    string;
  civilStatus:        number;
  countryBirth:       number;
  cuilNumber:         number;
  economicActivity:   number;
  placeOfBirth:       string;
  profession:         number;
  rutaNumber:         number;
  telephoneNumber:    string;
  telephoneNumberJob: string;
  webPage:            string;
}

export interface PaymentMethodSectionForm {
  cardNumber:         number;
  cbuNumber:          number;
  companyCreditCard:  number;
  expiration:         string;
  paymentMethod:      string;
}

export interface ProvincesInderCodes {
  codigoProvincia: string;
  codProvinciaInder: string;
}

export const GenericDataFormProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [additionalDataSectionForm] = useForm<AdditionalDataSectionForm>();
  const [paymentMethodSectionForm] = useForm<PaymentMethodSectionForm>();

  const [allCoveragesPerPlan, setAllCoveragesPerPlan] = useState<
    Array<{
      plan: string | RequestCoveragesByPlanActivities;
      coverages: Array<CoveragePerPlan>;
    }>
  >([]);
  const [allCoveragesPerPlanRisk, setAllCoveragesPerPlanRisk] = useState<
  Array<{
    plan: string  | RequestCoveragesByPlanActivities;
    coverages: Array<CoveragePerPlan>;
  }>
>([]);
  const [allLocalitiesPerProvince, setAllLocalitiesPerProvince] = useState<
    Array<{
      provinceCode: string;
      localities: Array<Locality>;
      localitiesOptions: Array<SelectOption>;
    }>
  >([]);
  const [additionalDataFormValues, setAdditionalDataFormValues] =
    useState<AdditionalDataSectionForm>();
  const [allLocalities, setAllLocalities] =
    useState<GetLocalitiesListPerProvinceResponse>();
  const [branchEconomyActivityList, setBranchEconomyActivityList] = useState<
    Array<SelectOption>
  >([]);
  const [canShowSpinner, setCanShowSpinner] = useState<boolean>(false);
  const [civilStatusList, setCivilStatusList] = useState<Array<SelectOption>>(
    []
  );
  const [clientDataFormValues, setclientDataFormValues] =
    useState<ClientDataSectionForm>();
  const [clientDataSectionForm] = useForm<ClientDataSectionForm>();
  const [countryList, setCountryList] = useState<Array<SelectOption>>([]);
  const [documentTypeList, setDocumentTypeList] =
    useState<Array<SelectOption>>();
  const [genderList, setGenderList] = useState<Array<SelectOption>>([]);
  const [isLoadingDocumentTypeList, setIsLoadingDocumentTypeList] =
    useState<boolean>(false);
  const [isLoadingGenderList, setIsLoadingGenderList] = useState<boolean>(true);
  const [isLoadingIvaConditionList, setIsLoadingIvaConditionList] =
    useState<boolean>(false);
  const [isLoadingListCreditCards, setIsLoadingListCreditCards] =
    useState<boolean>(true);
  const [isLoadingLocalities, setIsLoadingLocalities] =
    useState<boolean>(false);
  const [isLoadingNationalityList, setIsLoadingNationalityList] =
    useState<boolean>(true);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
    useState<boolean>(false);
  const [isLoadingProvinceList, setIsLoadingProvinceList] =
    useState<boolean>(false);
  const [isLoadingTypePersonList, setIsLoadingTypePersonList] =
    useState<boolean>(false);
  const [isLoadingTypeSocietyList, setIsLoadingTypeSocietyList] =
    useState<boolean>(true);
  const [ivaConditionList, setIVAConditionList] =
    useState<Array<SelectOption>>();
  const [provinceListInder, setProvinceListInder] = useState<Array<ProvincesInderCodes>>();
  const [listBankAccountByInsured, setListBankAccountByInsured] =
    useState<BankAccount>();
  const [listCreditCardInsured, setListCreditCardInsured] =
    useState<CreditCardsInsured>();
  const [listCreditCards, setListCreditCards] = useState<Array<SelectOption>>();
  const [localities, setLocalities] = useState<Array<SelectOption>>();
  const [nationalityList, setNationalityList] = useState<Array<SelectOption>>(
    []
  );
  const [paymentMethodFormValues, setPaymentMethodFormValues] =
    useState<PaymentMethodSectionForm>();
  const [paymentMethods, setPaymentMethods] = useState<Array<SelectOption>>([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState<
    Array<PaymentMethodForms>
  >([]);
  const [plansSelected, setPlansSelected] = useState<
    Array<string> | Array<RequestCoveragesByPlanActivities>
  >([]);
  const [professionList, setProfessionList] = useState<Array<SelectOption>>([]);
  const [provinceList, setProvinceList] = useState<Array<SelectOption>>();
  const [typePersonListByArticle, setTypePersonListByArticle] = useState<Array<{ article: string, options: Array<SelectOption> }>>([]);
  const [typeSocietyList, setTypeSocietyList] = useState<Array<SelectOption>>(
    []
  );

  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});

  const [tabsUserCanNotDelete, setTabsUserCanNotDelete] = useState<Array<number>>([]);

  const [userCanDeleteTabs, setUserCanDeleteTabs] = useState<boolean>(true);

  const [removedTabIndexes, setRemovedTabIndexes] = useState<Array<number>>([]);

  const [isErrorServices, setIsErrorServices] = useState<boolean>(false);

  const { quotationInfo } = useContext(QuotationInfoContext);

  const navigate = useNavigate();

  const typePersonList = useMemo(
    () => typePersonListByArticle.find(
      el => el.article === quotationInfo?.article
    )?.options, 
    [typePersonListByArticle, quotationInfo]
  );

  async function getTextSubcategory(
  coverage: CoveragePerPlan
): Promise<TextSubcategory[] | undefined> {
  try {
      const shouldLoadTextSubcategory = ['S', 'TRUE', '1'].includes(
        String(coverage.tieneTxtSubcategoria).toUpperCase()
      );

      if (!shouldLoadTextSubcategory) {
      return undefined;
    }
    if (
      quotationInfo?.article &&
      (Object.values(QuotationArticles) as string[]).includes(quotationInfo.article)
    ) {
      const params: RequestListTextSubcategory = {
        rama: quotationInfo.branch!,
        cobertura: coverage.codigo,
        moneda: CurrencyCode.PESOS_ARGENTINOS,
        producto: plansSelected[0].toString(),
        riesgo: coverage.codigoRiesgo
      };
      const response = await QuotationService.getListTextSubcategory(params);
      return response.data.subCategoriaDeTextos;
    }
  } catch (error) {
  }
}

  const getCoveragesPerPlan = useCallback(async () => {
  try {
    setCanShowSpinner(true);

    const promises = plansSelected
      .map((plan) => {
        if (typeof plan === 'string') {
          if (allCoveragesPerPlan.some((el) => String(el.plan) === plan))
            return null;

          return QuotationService.getCoveragesPerPlan(
            quotationInfo?.branch!,
            plan
          ).then(async (response) => {
            if (response.data?.coberturas) {
              const coberturas = await Promise.all(
                response.data.coberturas.map(async (coverage) => {
                  const subCategoriaDeTextos = await getTextSubcategory(coverage);
                  const coverageWithTexts = {
                    ...coverage,
                    subCategoriaDeTextos,
                  };
                  return coverageWithTexts;
                })
              );
              setAllCoveragesPerPlan((prevValues) => [
                ...prevValues,
                { plan, coverages: coberturas },
              ]);
            }
          });
        } else {
          if (
            allCoveragesPerPlan.some(
              (el) =>
                String((el.plan as RequestCoveragesByPlanActivities).plan) ===
                String(plan.plan)
            )
          )
            return null;

          return QuotationService.getCoveragesByPlanActivity(plan).then(
            async (response) => {
              if (response.data?.coberturas) {
                const coberturas = await Promise.all(
                  response.data.coberturas.map(async (coverage) => {
                    const subCategoriaDeTextos = await getTextSubcategory(coverage);
                    const coverageWithTexts = {
                      ...coverage,
                      subCategoriaDeTextos,
                    };
                    return coverageWithTexts;
                  })
                );

                setAllCoveragesPerPlan((prevValues) => [
                  ...prevValues,
                  { plan, coverages: coberturas },
                ]);
              }
            }
          );
        }
      })
      .filter(Boolean);

    await Promise.all(promises);
  } catch (e) {
    console.error(e);
  } finally {
    setCanShowSpinner(false);
  }
}, [
  setCanShowSpinner,
  setAllCoveragesPerPlan,
  quotationInfo,
  plansSelected,
  allCoveragesPerPlan,
]);


  const getCoveragesPerPlanRisk = useCallback(async (riskCode: string, activityCode: string) => {    
    try {
      setCanShowSpinner(true);
  
      const promises = plansSelected
        .map((plan) => {
          const planId = typeof plan === 'string' ? plan : plan.plan;
  
          const isAlreadyFetched = allCoveragesPerPlanRisk.some(
            (el) => String((el.plan as any).plan ?? el.plan) === String(planId)
          );
  
          if (isAlreadyFetched) return null;
  
          return QuotationService.getCoveragesPerPlanRisk(
            quotationInfo?.branch!,
            String(planId),
            riskCode,
            activityCode
          ).then((response) => {
            if (response.data) {
              setAllCoveragesPerPlanRisk((prevValues) => [
                ...prevValues,
                { plan, coverages: response.data.coberturas },
              ]);
            }
          });
        })
        .filter(Boolean);
  
      await Promise.all(promises);
      setCanShowSpinner(false);
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    setCanShowSpinner,
    setAllCoveragesPerPlanRisk,
    quotationInfo,
    plansSelected,
    allCoveragesPerPlanRisk,
  ]);
  

  const getTypePersonList = useCallback(
    async (article: string) => {
      try {
        setIsLoadingTypePersonList(true);

        if(typePersonListByArticle?.find(el => String(el.article) === String(article))) return;
        
        const response = await QuotationService.getPeopleTypeListPerArticle(
          article
        );

        if (response.data) {
          const list = response.data.tiposDePersona.map(
            ({ codigo, descripcion }) => ({ label: descripcion, value: codigo })
          );

          setTypePersonListByArticle((prevValues) => [...prevValues, { article, options: list }]);
        }
      } catch {
      } finally {
        setIsLoadingTypePersonList(false);
      }
    },
    [
      setIsLoadingTypePersonList, 
      setTypePersonListByArticle,
      typePersonListByArticle,
    ]
  );

  const getIVAConditionList = useCallback(async () => {
    try {
      setIsLoadingIvaConditionList(true);
      const response = await MasterService.getIVATypeList();

      if (response.data) {
        const list = response.data.condicionesIva.map(
          ({ codigo, descripcion }) => ({ label: descripcion, value: codigo })
        );
        setIVAConditionList(list);
      }
    } catch {
    } finally {
      setIsLoadingIvaConditionList(false);
    }
  }, [setIsLoadingIvaConditionList, setIVAConditionList]);

  const getProvinceList = useCallback(async () => {
    try {
      setIsLoadingProvinceList(true);
      const response = await MasterService.getProvinceList();

      if (response.data) {
        const provinceList = response.data.provincias.map(
          ({ codigoProvincia, descripcionProvincia, codProvinciaInder }) => ({
            label: descripcionProvincia,
            value: codigoProvincia,
          })
        );
        const provinceListInder = response.data.provincias.map(
          ({ codProvinciaInder, codigoProvincia }) => ({
            codigoProvincia: codigoProvincia,
            codProvinciaInder: codProvinciaInder
          })
        );
        setProvinceList(provinceList);
        setProvinceListInder(provinceListInder);
      }
    } catch {
    } finally {
      setIsLoadingProvinceList(false);
    }
  }, [setIsLoadingProvinceList, setProvinceList, setProvinceListInder]);

  const getLocalitiesPerProvince = useCallback(
    async (provinceCode: string) => {
      try {
        setIsLoadingLocalities(true);

        if (
          allLocalitiesPerProvince.find(
            (el) => String(el.provinceCode) === String(provinceCode)
          )
        )
          return;

        const response = await MasterService.getLocalitiesListPerProvince(
          provinceCode
        );

        if (response.data) {
          const list = response.data.localidadesPorProvincia.map(
            ({ localidad, codigoPostal, sufijoCodigoPostal }) => ({
              label: `${localidad} (${codigoPostal}-${sufijoCodigoPostal})`,
              value: `${sufijoCodigoPostal}${codigoPostal}`,
            })
          );
          setLocalities(list);
          setAllLocalities(response.data);

          if (
            !allLocalitiesPerProvince.find(
              (el) => String(el.provinceCode) === String(provinceCode)
            )
          ) {
            setAllLocalitiesPerProvince((prevValues) => [
              ...prevValues,
              {
                provinceCode,
                localities: response.data.localidadesPorProvincia,
                localitiesOptions: list,
              },
            ]);
          }
        }
      } catch {
      } finally {
        setIsLoadingLocalities(false);
      }
    },
    [
      setIsLoadingLocalities,
      setLocalities,
      setAllLocalitiesPerProvince,
      allLocalitiesPerProvince,
    ]
  );

  const getDocumentTypeList = useCallback(async () => {
    try {
      setIsLoadingDocumentTypeList(true);
      const response = await MasterService.getDocumentTypeList();

      if (response) {
        setDocumentTypeList(response.data);
      }
    } catch {
    } finally {
      setIsLoadingDocumentTypeList(false);
    }
  }, [setIsLoadingDocumentTypeList, setDocumentTypeList]);

  const getTypeSocietyList = useCallback(async () => {
    try {
      setIsLoadingTypeSocietyList(true);
      const response = await MasterService.getListTypesOfSociety();

      if (response.data) {
        const typesSociety = response.data.sociedades.map(
          ({ descripcion, codigo }) => ({
            label: descripcion,
            value: codigo.toString(),
          })
        );

        setTypeSocietyList(typesSociety);
      }
    } catch {
    } finally {
      setIsLoadingTypeSocietyList(false);
    }
  }, [setIsLoadingTypeSocietyList, setTypeSocietyList]);

  const getCivilStatusList = useCallback(async () => {
    try {
      const response = await MasterService.getListCivilStatus();

      if (response) {
        setCivilStatusList(response.data);
      }
    } catch {}
  }, [setCivilStatusList]);

  const getProfessionList = useCallback(async () => {
    try {
      const response = await MasterService.getListProfessions();

      if (response.data) {
        const professions = response.data.profesiones.map(
          ({ descripcion, codigo }) => ({
            label: descripcion,
            value: codigo.toString(),
          })
        );

        setProfessionList(professions);
      }
    } catch {}
  }, [setProfessionList]);

  const getBranchEconomyActivityList = useCallback(async () => {
    try {
      const response = await MasterService.getListBranchEconomyActivity();

      if (response.data) {
        const economyActivities = response.data.ramasActividadEconomica.map(
          ({ descripcion, codigo }) => ({
            label: descripcion,
            value: codigo.toString(),
          })
        );

        setBranchEconomyActivityList(economyActivities);
      }
    } catch {}
  }, [setBranchEconomyActivityList]);

  const getCountriesList = useCallback(async () => {
    try {
      const response = await MasterService.getListCountries();

      if (response.data) {
        const economyActivities = response.data.paises.map(
          ({ descripcionPais, codigoPais }) => ({
            label: descripcionPais,
            value: codigoPais,
          })
        );

        setCountryList(economyActivities);
      }
    } catch {}
  }, [setCountryList]);

  const getListBankAccountByInsured = useCallback(
    async (nroAsegurado: string) => {
      try {
        const response = await ProducersService.getBankAccount(nroAsegurado);

        if (response.data) {
          setListBankAccountByInsured(response.data);
        }
      } catch {}
    },
    [setListBankAccountByInsured]
  );

  const getListCreditCardByInsured = useCallback(
    async (nroAsegurado: string) => {
      try {
        if (nroAsegurado !== "0" && nroAsegurado !== "") {
          const response = await ProducersService.getCreditCardsByInsured(
            nroAsegurado
          );

          if (response.data) setListCreditCardInsured(response.data);
        }
      } catch {}
    },
    [setListCreditCardInsured]
  );

  const getGenderList = useCallback(async () => {
    try {
      const response = await MasterService.getGenderList();

      if (response) {
        setGenderList(response.data);
        setIsLoadingGenderList(false);
      }
    } catch {}
  }, [setGenderList, setIsLoadingGenderList]);

  const getNationalityList = useCallback(async () => {
    try {
      const response = await MasterService.getNationalityList();

      if (response.data) {
        const nationalities = response.data.nacionalidades.map(
          ({ codigo, nacionalidad }) => ({
            label: nacionalidad,
            value: codigo.toString(),
          })
        );

        setNationalityList(nationalities);
        setIsLoadingNationalityList(false);
      }
    } catch {}
  }, [setNationalityList, setIsLoadingNationalityList]);

  const getListCreditCards = useCallback(async () => {
    try {
      const response = await MasterService.getListCreditCards();
      if (response?.data?.empresasTarjetaDeCredito) {
        const optionsCreditCards: Array<SelectOption> =
          response.data.empresasTarjetaDeCredito.map(
            ({ idEmpresa, empresa, cantidadDeDigitos }: any) => ({
              label: empresa,
              value: idEmpresa,
              cantidadDeDigitos: cantidadDeDigitos,
            })
          );

        setListCreditCards(optionsCreditCards);
        setIsLoadingListCreditCards(false);
      }
    } catch {}
  }, [setListCreditCards, setIsLoadingListCreditCards]);

  const getGenericDataForm = useCallback(async () => {
    setCanShowSpinner(true);

    const queries = [];

    if (!typePersonList) {
      queries.push(getTypePersonList(quotationInfo?.article!));
    }

    if (!ivaConditionList) {
      queries.push(getIVAConditionList());
    }

    if (!provinceList) {
      queries.push(getProvinceList());
    }

    if (!documentTypeList) {
      queries.push(getDocumentTypeList());
    }

    if (typeSocietyList.length === 0) {
      queries.push(getTypeSocietyList());
    }

    if (civilStatusList.length === 0) {
      queries.push(getCivilStatusList());
    }

    if (branchEconomyActivityList.length === 0) {
      queries.push(getBranchEconomyActivityList());
    }

    if (countryList.length === 0) {
      queries.push(getCountriesList());
    }

    if (professionList.length === 0) {
      queries.push(getProfessionList());
    }

    if (genderList.length === 0) {
      queries.push(getGenderList());
    }

    if (nationalityList.length === 0) {
      queries.push(getNationalityList());
    }

    Promise.all(queries).finally(() => {
      setCanShowSpinner(false);
    });
  }, [
    branchEconomyActivityList,
    civilStatusList,
    countryList,
    documentTypeList,
    genderList,
    getBranchEconomyActivityList,
    getCivilStatusList,
    getCountriesList,
    getDocumentTypeList,
    getGenderList,
    getIVAConditionList,
    getNationalityList,
    getProfessionList,
    getProvinceList,
    getTypePersonList,
    getTypeSocietyList,
    ivaConditionList,
    nationalityList,
    professionList,
    provinceList,
    provinceListInder,
    quotationInfo,
    typePersonList,
    typeSocietyList,
  ]);

  const saveQuotation = useCallback(
    async (client: QuotationClient, additionalDataClientSelected: AdditionalDataClient) => {
      setCanShowSpinner(true);

      const clientParam: ClientParam = {
        nroAsegurado: Number(checkValueAndReturnDefault(client?.nroAsegurado, 0)),
        nombreCompleto: checkValueAndReturnDefault(client?.nombre, ''),
        codigoCondicionIva: Number(checkValueAndReturnDefault(client?.condicionIVA, 0)),
        tipoDePersona: checkValueAndReturnDefault(client?.tipoPersona, ''),
        codigoPostal: Number(checkValueAndReturnDefault(client?.codigoPostal, 0)),
        sufijoCodigoPostal: Number(checkValueAndReturnDefault(client?.sufijoCodigoPostal, 0)),
        cuit: Number(checkValueAndReturnDefault(client?.cuit, 0)),
        tipoDeDocumento: Number(checkValueAndReturnDefault(client?.tipoDocumento, 0)),
        numeroDeDocumento: Number(checkValueAndReturnDefault(client?.nroDocumento, 0)),
        provincia: checkValueAndReturnDefault(client?.codProvincia, ''),
        codProvinciaInder: Number(checkValueAndReturnDefault(client?.codProvinciaInder, 0)),
        telefonoParticular: checkValueAndReturnDefault(additionalDataClientSelected?.telefonoParticular, ''),
        telefonoLaboral: checkValueAndReturnDefault(additionalDataClientSelected?.telefonoLaboral, ''),
        telefonoCelular: checkValueAndReturnDefault(additionalDataClientSelected?.telefonoCelular, ''),
        cuil: Number(checkValueAndReturnDefault(client?.cuit, 0)),
        paginaWeb: checkValueAndReturnDefault(additionalDataClientSelected?.paginaWeb, ''),
        fechaNacimiento: checkValue(additionalDataClientSelected?.fechaNacimiento) ?  Number(dayjs(additionalDataClientSelected?.fechaNacimiento).format('YYYYMMDD')) : 0,
        idPaisNacimiento: Number(checkValueAndReturnDefault(additionalDataClientSelected?.idPaisNacimiento, 0)),
        idEstadoCivil:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.idEstadoCivil, 0)),
        idRamaEconomica:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.idRamaEconomica, 0)),
        numeroRuta:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.numeroRuta, 0)),
        email: checkValueAndReturnDefault(additionalDataClientSelected?.email, ''),
        domicilio: checkValueAndReturnDefault(additionalDataClientSelected?.domicilio, ''),
        codigoNacionalidad:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.codigoNacionalidad, 0)),
        codigoSexo:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.codigoSexo, 0)),
        fechaInscripcion: checkValue(additionalDataClientSelected?.fechaInscripcion) ?  Number(dayjs(additionalDataClientSelected?.fechaInscripcion).format('YYYYMMDD')) : 0,
        numeroInscripcion:   Number(checkValueAndReturnDefault(additionalDataClientSelected?.numeroInscripcion, 0)),
        fechaContrato: checkValue(additionalDataClientSelected?.fechaContrato) ?  Number(dayjs(additionalDataClientSelected?.fechaContrato).format('YYYYMMDD')) : 0,
        agentePercepcion: checkValueAndReturnDefault(additionalDataClientSelected?.agentePercepcion, ''),
        agenteRetencion: checkValueAndReturnDefault(additionalDataClientSelected?.agenteRetencion, ''),
        idProfesion:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.idProfesion, 0)),
        lugarNacimiento: checkValueAndReturnDefault(additionalDataClientSelected?.lugarNacimiento, ''),
        codigoTipoSociedad:  Number(checkValueAndReturnDefault(additionalDataClientSelected?.codigoTipoSociedad, 0))
      }

      const params: SaveQuotationServiceRequest = {
        token: quotationInfo?.token,
        codigoAsegurado: client?.nroAsegurado,
        codigoIva: client?.condicionIVA,
        codigoPostal: client?.codigoPostal,
        cuit: client?.cuit,
        nombreAsegurado: client?.nombre,
        numeroDocumento: client?.nroDocumento,
        sufijoPostal: client?.sufijoCodigoPostal,
        tipoDocumento: client?.tipoDocumento,
        tipoPersona: client?.tipoPersona,
        version: 1,
        cliente: clientParam
      };

      try {        
        await QuotationService.saveQuotation(params);
        const messageSuccess = `La cotización ${quotationInfo?.token} fue guardada con éxito!`;
        displaySuccessMessage(messageSuccess, 1.5, () => {
          setCanShowSpinner(false);
          navigate('/cotizador/cotizaciones-guardadas');
        });
      } catch (error) {
        displayErrorMessage(
          'Ha ocurrido un error al intentar guardar la cotización, por favor verifique todos los datos, e inténtelo de nuevo.'
        );
      }
    },
    [quotationInfo]
  );

  const getLocalitiesByProvinceFromState = useCallback(
    (provinceCode: string) =>
      allLocalitiesPerProvince.find(
        (el) => String(el.provinceCode) === String(provinceCode)
      )?.localitiesOptions,
    [allLocalitiesPerProvince]
  );

  const getRelationWithOtherCoverage = useCallback(
    (
      id: string,
      allCoverages: Array<CoveragePerPlan>
    ):
      | { codigoCoberturaRelacionada: string; codigoRiesgo: string }
      | undefined => {
      const coverage = allCoverages?.find((el) => el.id === id);
      return {
        codigoCoberturaRelacionada: coverage!.codigoCoberturaRelacionada,
        codigoRiesgo: coverage!.codigoRiesgo,
      };
    },
    []
  );

  const setPlansSelectedByTabs = useCallback(
    (tabs: Array<any>, form?: FormInstance<any>) => {
      const plansSelected: Array<string> = [];

      tabs?.forEach(({ key }) => {
        const planSelected = form?.getFieldValue(`plan-tab-${key}`);
        if (!planSelected) return;
        if (plansSelected.includes(planSelected)) return;
        plansSelected.push(planSelected);
      });

      if (!plansSelected.length) return;

      setPlansSelected(plansSelected);
    },
    [setPlansSelected]
  );

  const getLocalityInfo = useCallback(
    (provinceCode: string, postalCodeSufix: string, postalCode: string) =>
      allLocalitiesPerProvince
        .find((el) => String(el.provinceCode) === String(provinceCode))
        ?.localities.find(
          (el) =>
            String(el.codigoPostal) === String(postalCode) &&
            String(el.sufijoCodigoPostal) === String(postalCodeSufix)
        ),
    [allLocalitiesPerProvince]
  );

  const updateFormsAfterRemoveTabs = useCallback(
    (tabsIndexes: Array<number>, forms: Array<FormInstance<any>>, oldFieldsValues?: {[key: string]: any}) => {
      forms.forEach((form) => {
        
        const oldFields = oldFieldsValues ?? form.getFieldsValue();
        
        const newFields = reIndexComponents(oldFields, tabsIndexes);
        
        form.setFieldsValue(undefined);

        form.setFieldsValue(newFields);
      });
    },
    []
  );

  useEffect(() => {
    if (plansSelected?.length) getCoveragesPerPlan();
  }, [plansSelected, getCoveragesPerPlan]);

  const values = useMemo(
    () => ({
      additionalDataFormValues,
      additionalDataSectionForm,
      allCoveragesPerPlan,
      allCoveragesPerPlanRisk,
      allLocalities,
      allLocalitiesPerProvince,
      branchEconomyActivityList,
      canShowSpinner,
      civilStatusList,
      clientDataFormValues,
      clientDataSectionForm,
      countryList,
      documentTypeList,
      genderList,
      getCoveragesPerPlan,
      getCoveragesPerPlanRisk,
      getGenericDataForm,
      getListBankAccountByInsured,
      getListCreditCardByInsured,
      getListCreditCards,
      getLocalitiesByProvinceFromState,
      getLocalitiesPerProvince,
      getLocalityInfo,
      getRelationWithOtherCoverage,
      isLoadingDocumentTypeList,
      isLoadingGenderList,
      isLoadingIvaConditionList,
      isLoadingListCreditCards,
      isLoadingLocalities,
      isLoadingNationalityList,
      isLoadingPaymentMethods,
      isLoadingProvinceList,
      isLoadingTypePersonList,
      isLoadingTypeSocietyList,
      isErrorServices,
      ivaConditionList,
      listBankAccountByInsured,
      listCreditCardInsured,
      listCreditCards,
      localities,
      nationalityList,
      paymentMethodFormValues,
      paymentMethods,
      paymentMethodSectionForm,
      paymentMethodsList,
      plansSelected,
      professionList,
      provinceList,
      provinceListInder,
      setIsErrorServices,
      saveQuotation,
      setAdditionalDataFormValues,
      setCanShowSpinner,
      setclientDataFormValues,
      setGenderList,
      setIsLoadingGenderList,
      setIsLoadingNationalityList,
      setIsLoadingPaymentMethods,
      setListBankAccountByInsured,
      setListCreditCardInsured,
      setListCreditCards,
      setLocalities,
      setNationalityList,
      setPaymentMethodFormValues,
      setPaymentMethods,
      setPaymentMethodsList,
      setPlansSelected,
      setPlansSelectedByTabs,
      typePersonList,
      typeSocietyList,
      updateFormsAfterRemoveTabs,
      tabsUserCanNotDelete, 
      setTabsUserCanNotDelete,
      formValues, 
      setFormValues,
      removedTabIndexes,
      setRemovedTabIndexes,
      userCanDeleteTabs,
      setUserCanDeleteTabs,
    }),
    [
      additionalDataFormValues,
      additionalDataSectionForm,
      allCoveragesPerPlan,
      allCoveragesPerPlanRisk,
      allLocalities,
      allLocalitiesPerProvince,
      branchEconomyActivityList,
      canShowSpinner,
      civilStatusList,
      clientDataFormValues,
      clientDataSectionForm,
      countryList,
      documentTypeList,
      genderList,
      getCoveragesPerPlan,
      getCoveragesPerPlanRisk,
      getGenericDataForm,
      getListBankAccountByInsured,
      getListCreditCardByInsured,
      getListCreditCards,
      getLocalitiesByProvinceFromState,
      getLocalitiesPerProvince,
      getLocalityInfo,
      getRelationWithOtherCoverage,
      isLoadingDocumentTypeList,
      isLoadingGenderList,
      isLoadingIvaConditionList,
      isLoadingListCreditCards,
      isLoadingLocalities,
      isLoadingNationalityList,
      isLoadingPaymentMethods,
      isLoadingProvinceList,
      isLoadingTypePersonList,
      isLoadingTypeSocietyList,
      isErrorServices,
      ivaConditionList,
      listBankAccountByInsured,
      listCreditCardInsured,
      listCreditCards,
      localities,
      nationalityList,
      paymentMethodFormValues,
      paymentMethods,
      paymentMethodSectionForm,
      paymentMethodsList,
      plansSelected,
      professionList,
      provinceList,
      provinceListInder,
      setIsErrorServices,
      saveQuotation,
      setAdditionalDataFormValues,
      setCanShowSpinner,
      setGenderList,
      setIsLoadingGenderList,
      setIsLoadingNationalityList,
      setIsLoadingPaymentMethods,
      setListBankAccountByInsured,
      setListCreditCardInsured,
      setListCreditCards,
      setLocalities,
      setNationalityList,
      setPaymentMethodFormValues,
      setPaymentMethods,
      setPaymentMethodsList,
      setPlansSelected,
      setPlansSelectedByTabs,
      typePersonList,
      typeSocietyList,
      updateFormsAfterRemoveTabs,
      tabsUserCanNotDelete, 
      setTabsUserCanNotDelete,
      formValues, 
      setFormValues,
      removedTabIndexes,
      setRemovedTabIndexes,
      userCanDeleteTabs,
      setUserCanDeleteTabs,
    ]
  );

  return (
    <GenericDataFormContext.Provider value={values}>
      {children}
    </GenericDataFormContext.Provider>
  );
};
